import { CignaStandardOutcome, ClaimSystemCode } from '@cigna/chcp/shared/util';
import { NavigationExtras } from '@angular/router';

export interface MetaData {
  serviceReferenceId?: string;
  outcome: CignaStandardOutcome;
}

export interface PrecertData {
  url: string;
  sessionKey: string;
}

export interface PatientDetailRequest {
  key: string;
  coverageType?: string;
  asof?: string;
}

export interface SearchInfoResponse {
  multipleResponse: boolean;
  noneMatched: boolean;
  singleResponse: boolean;
}

export interface PatientSearchObject {
  asOfDate: string;
  patients: PatientSearchRow[];
}

export interface InactivePatientSearchObject {
  uniqueIdentifier: number;
  asOfDate: string;
  patient: PatientSearchRow;
}
export interface PatientSearchRow {
  patientId: string;
  patientDob: string;
  patientFirstName: string;
  patientLastName: string;
}

export interface PatientResultObject {
  uniqueIdentifier?: number;
  display: {
    patientId: string;
    dob: string;
    firstName: string;
    lastName: string;
    coverageFrom: string;
    coverageTo: string;
    coverageStatus: string;
    account: string;
    notes: string;
    isInactive: boolean;
    compositeKey: string;
    restricted: boolean;
    showFlag: boolean;
    showLink: boolean;
    defaultTab?: string;
  };
  detail: any;
}

export interface PatientDetailsData {
  alliances: Alliance;
  ccfBenefit: CcfBenefit;
  coverageDetail: CoverageDetail;
  idcardDetail: IdCardDetails;
  subscriberDetail: UserDetail;
  patientDetail: PatientDetailsObj;
  serviceContact: ServiceContact[];
  planLevelAccumulation: PlanLevelAccumulation[];
  serviceLevelAccumulation?: ServiceLevelAccumulation;
  primaryCareDetails: PrimaryCareDetails;
  cobDetails: CobDetails[];
  maternityBenefitDetails: MaternityBenefitDetails;
  healthWellness: HealthWellness;
  pharmacyBenefitDetails: PharmacyBenefitDetails;
  notificationDetail: NotificationDetail;
  productDetails: ProductDetail;
  frequencyAgePlanDetails: FrequencyAgePlanDetails;
  visionServices: VisionServices;
  infertilityServices: InfertilityServices;
  reference: BRNPayload | null;
}

export interface IdCardDetails {
  accountID: string;
  backImageFlag: boolean;
  backImageGUID: string;
  backImageUrl: string;
  benOptionCode: string;
  cardType: string;
  claimSystemCode: string;
  contentSearchBeansList: string[];
  coverageFromDate: string;
  frontImageFlag: boolean;
  frontImageGUID: string;
  frontImageUrl: string;
  idCardExtensionCode: string;
  idNumber: string;
  isIFP: string;
  issueDate: string;
  medicalPlan: string;
  memberCoverageDate: string;
  patientID: string;
  patientName: string;
  pharmacyAvailable: string;
  planName: string;
  productType: string;
  relationshipCode: string;
  selectPharmacyAvailable: string;
}
export interface NotificationDetail {
  mmmInd: string;
  displayOutPatientPreCertMngmtLevels: boolean;
  outPatientPreCertMngmtLevelList: NotificationData[] | null;
  indemnity: boolean;
  prodInNetwork: boolean;
  carveOutInfo: CarveOutInfo;
  surefitEast: boolean;
  surefitWest: boolean;
  routerUrl: string;
  routerUrlInput: string;
  notificationData: NotificationData[];
}
export interface NotificationData {
  inNetworkValue: string;
  label: string;
  outNetworkValue: string;
}
export interface CarveOutInfo {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string;
  carveOutMessage: string | null;
  entTypeQual: string;
  idCodeQual: string;
  serviceTypeCode: string;
  suffix: string;
  address: string;
  contact: string;
}

export interface CcfBenefit {
  footNotes: string[];
  ccfbenefitFundList: CcfBenefitFundList[];
  ccfType: string;
}
export interface CcfBenefitFundList {
  carryOverAmount: string;
  carryOverPercentage: string;
  currentAmount: string;
  fundType: string;
  goalAmount: string;
  note: string;
  note2: string;
  fundTypeWithValues: boolean;
  coverageList: CcfCoverageList[];
}
export interface CcfCoverageList {
  eligibilityServiceType: string;
  eligibilityServiceValue: string;
  label: string;
  type: string;
}

// TODO: resolve CHCP disables by bot
// eslint-disable-next-line import/export
export interface CobDetails {
  policyNo: string;
  verifiedOn: string;
  effectiveFrom: string;
  effectiveTill: string;
  carrier: string;
  coverageType: string;
  primacy: string;
}
export interface CoverageDetail {
  accountNumber: string;
  accountName: string;
  clientId: string;
  effectiveFrom: string;
  effectiveTill: string;
  initialCoverageDate: string;
  status?: string;
  coverageStatus: string;
  compliance?: Compliance;
  plan: Plan;
  hasPRN?: boolean;
  hasPDP: boolean;
  hasIFP: boolean;
  hasCSN?: boolean;
  hasMHSR?: boolean;
  hasBayCareGroup: boolean;
  coverageType: string;
  claimSystemCode: ClaimSystemCode;
  networkDetails: NetworkDetails;
  networkDescription?: string;
  otherInsurance: string;
  medicare?: MedicareDetails;
  otherInsuranceVerifiedOn: string;
  pediatricInd: string;
  utilizationDataNotAvlble?: boolean;
  isMultipleCoverage?: boolean;
  hasMultipleActive?: boolean;
  hasCoveredBenefits?: boolean;
  coverages: string[];
  isAccountRestricted?: boolean;
  cignaCareNetwork?: {
    tier: string;
    nonReviewedSpltyPayCode: string;
  };
  medicalManagementPlan?: string;
  referralCode?: string;
  hasConflictingStatus?: boolean;
  erisaApplicable?: boolean;
  ohip?: string;
  umAuthSystem: string;
}

export interface Compliance {
  contraceptiveOnly: boolean;
  pendingAtHIX: string;
}

export interface MedicareDetails {
  contractNumber: string;
  groupNumber?: string;
  clientId?: string;
  benefitOptionode?: string;
  planBenefitPackageNumber: string;
  segmentNumber: string;
  healthPlanName: string;
  operationalMarket: string;
  regulatoryMarket: string;
  specialNeedPlanType: string;
  egwpIndicator: string;
  cobDetails: CobDetails[];
  pcpRequired: string;
  familyCode?: string;
}

export interface DentalOfficeDetails {
  effectiveFrom: string;
  effectiveTill: string;
  id: string;
  name: string;
}

export interface CostEstimationDetail {
  patientId: string;
  dob: string;
  accountName: string;
  accountNumber: string;
  dos?: string;
  ohpuser: boolean;
  placeofService: any[];
  serviceTypes: CoceServiceTypes[];
  serviceLocation?: CoceServiceLocation[];
  clinicalCategories?: [
    {
      code: string;
      description: string;
      coverageType: string;
      procedures: [
        {
          code: string;
          description: string;
        },
      ];
    },
  ];
  modifiers: string[];
  tins: Tin[];
}

export interface CoceServiceTypes {
  eproBenType: string;
  eproBenType1?: string;
  posCode: string;
  serviceTypeCode: string;
  serviceTypeDesc: string;
  serviceTypeName: string;
  displayServiceTypeDesc: string;
  shortTextMessage?: string;
  nonCore?: string;
  serviceType?: string;
  benefitName?: string;
}

export interface CoceServiceLocation {
  code: string;
  description: string;
}

export interface Tin {
  name: string;
  number: string;
  contracted: boolean;
}

export interface PatientEstimateCostResult {
  isInpatient: boolean;
  procedureList: [
    {
      code: string;
      description: string;
      modifier1: string;
      modifier2: string;
      unit: string;
    },
  ];
  careLinkProdName: string;
  clinicalCategory: string;
  clinicalCategoryName: string;
  codeDescription: string;
  codeDescriptionName: string;
  confidenceLevelTotal: string;
  dateOfEstimate: string;
  dateOfService: string;
  deductActual: string;
  deductApplied: string;
  deductibleBeforeCopay: string;
  serviceTypeName: string;
  placeOfServiceName: string;
  firstName: string;
  lastName: string;
  hraFundBalance: string;
  inNetwork: string;
  patientCoInsuranceRate: string;
  displayServiceTypeDesc: string;
  patientID: string;
  placeOfServiceSelected: string;
  planName: string;
  planType: string;
  pointDeductibleAppliedFlag: string;
  serviceType: string;
  servicingProvider: string;
  servicingProviderName: string;
  includeAnesthesiology: true;
  patientCoInsurance: string;
  patientCoPayment: string;
  patientDeductible: string;
  patientLiability: string;
  pointEstimateTotal: string;
  pointFundAppliedAmount: string;
  pointRemainingPatientLiability: string;
}

export interface ServiceProcedure {
  code: string;
  description: string;
  isChecked?: boolean;
}

export interface Plan {
  effectiveFrom: string;
  effectiveTill: string;
  planYears: PlanYears;
  type: string;
  fundingType: string;
  isIndemnity: boolean;
  progressiveRegressiveInd?: string;
  deductibleSequence?: string;
  benefitOption?: string;
  branch?: string;
  rxCarveOutVendor?: string;
  classNumber?: string;
  planVariationIdentifier?: string;
  pathwellIndicator?: string;
}

export interface PlanYears {
  effectiveFrom: string;
  effectiveTill: string;
  renewalType: string;
}

export interface NetworkDetails {
  networkId: string;
  networkName: string;
  networkType: string;
  effectiveFrom: string | null;
  effectiveTill: string | null;
  npoId: string;
  npoName: string;
  mpoId: string;
  mpoName: string;
  porgId: string;
  seamlessIndicator: string;
  baseNetworkId: string;
  iCareNetworkId: string;
  hmoDetails: HmoDetails;
  isAllegianceMember: boolean;
  isHAPMember: boolean;
  inOutIndicator: string;
  dnspEligible: string | null;
  isNetworkTiered: boolean;
  tier: Tier | null;
  superNetworkId?: string;
  pcp?: {
    providerIdentity: {
      id: string;
      type: string;
    };
    pcpStatus: string;
    hasPCP: boolean;
    assignmentType: string;
    cancelDate: string;
    effectiveDate: string;
  };
  ecnNetworkDetails?: EcnNetworkDetails;
}

export interface EcnNetworkDetails {
  csnNetworkDetails: { csnNetwork: CsnDetail[] };
  ecnId: string;
}

export interface CsnDetail {
  csnId: string;
  displayOrderCd: string;
  displayOrderDesc: string;
  tierName: string;
}

export interface Tier {
  tier1: string;
  tier2: string;
}

export interface HmoDetails {
  orgName?: string;
  code: string;
  networkLob: string;
  lob: string;
  networkId: string;
  networkIdCode?: string;
  networkDesc: string;
  contact: Contact;
}

export interface Address {
  type: string;
  lines: string[];
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface Phone {
  number: string;
  type: string;
}

export interface ProductDetail {
  typeCode: string;
  familyCode: string;
  hraFundBalance?: string;
  isV2V: boolean;
  isIFP: boolean;
  hasHRA: boolean;
  hasHSA: boolean;
  hasFSA: boolean;
  embeddedProducts: EmbeddedProducts[];
  name: string;
}

export interface EmbeddedProducts {
  productCode: string;
  effectiveOn: string;
  endsOn: string;
}

export interface Alliance {
  hasMVP: boolean;
  partners: Partner[];
}

export interface Partner {
  roleOfCigna: string;
  effectiveOn: string;
  endsOn: string;
  coordinatingParty: string;
  supportingParty: string;
  thirdPartyAdmin: string;
}

//  TODO: resolve CHCP disables by bot
//  eslint-disable-next-line import/export
export interface CobDetails {
  policyNo: string;
  verifiedOn: string;
  effectiveOn: string;
  endsOn: string;
  carrier: string;
  coverageType: string;
  primacy: string;
}

export interface UserDetail {
  firstName: string;
  lastName: string;
  middleName: string;
  dob: string;
}

export interface PatientDetailsObj {
  asOfDate: string;
  details: PatientDetails;
}

export interface PatientDetails extends UserDetail {
  id: string;
  ami?: string;
  fullName: string;
  gender: string;
  relationship: string;
  addresses: Address[];
  isPolicyHolder: boolean;
  isInternationalMember: boolean;
  idCardExtensionCode?: string;
  restricted?: boolean;
  internalProcessingId?: string;
}

export interface Contact {
  type?: string;
  name: string;
  addresses: Address[];
  phones: Phone[];
}

export interface ServiceContact extends Contact {
  id?: string;
  effectiveDate?: string;
  group?: string;
  web?: string;
  webName?: string;
}

export interface PlanLevelAccumulation {
  type: string;
  notes: string[];
  benefits: {
    [key: string]: AccumulationDetail;
  };
}

export interface BenefitReportDetails {
  patientChargeSheet: {
    fileSeqNum: string;
    ctrl: string;
    docName: string;
    docTypCode: string;
    expireDt: string;
    fileTypeCode: string;
  } | null;
  stateBenefitInfo: {
    fileSeqNum: string;
    ctrl: string;
    docName: string;
    docTypCode: string;
    expireDt: string;
    fileTypeCode: string;
  } | null;
  showBenefitsInd: boolean;
}

export interface ServiceLevelAccumulation {
  services: Service[] | [];
}

export interface Service {
  serviceTypeCode: string;
  serviceTypeDesc: string;
  variations: Variation[];
  specialtyVariation?: { [key: string]: Variation[] };
}

export interface Variation {
  diagnosisCode?: string;
  diagnosisDesc?: string;
  shortDesc?: string;
  benefitServiceId?: string;
  serviceName: string;
  tags: string[];
  posCode: string;
  posDesc: string;
  procedureCode?: string;
  procedureDesc?: string;
  priorAuthorizationRequired?: priorAuthRequired;
  isPriorAuthRequired?: AuthRequiredData[];
  notes: string[];
  specialty?: string;
  benefits: {
    [key: string]: AccumulationDetail | undefined;
  };
}

export interface ClaimSystemData {
  claimSystem: string;
  coverage: string;
}

export interface PreAuthData {
  procedureCode: string;
  placeOfService: string;
  isPriorAuthRequired: AuthRequiredData[];
  diagnosisCode: string;
}

export interface UpdatedVariation {
  serviceName: string;
  tags: string[];
  posCode: string;
  posDesc: string;
  notes: string[];
  benefits: Accumulation[][];
  specialty?: string;
  isPriorAuthRequired?: AuthRequiredData[];
}

export interface AccumulationDetail {
  coveredServices?: string[];
  accumulations?: Accumulation[];
}

export interface DentalAccumulationDetail {
  coveredServices?: string[];
  accumulations: DentalAccumulation[];
}

export interface Accumulation {
  desc: string;
  amount: string;
  met: string | null;
  remaining: string | null;
  covers?: string;
  networkType: string;
  notes: string[];
  cignaCareNetwork: boolean;
  coveredServices: string[];
  tierIndex?: string | null;
  tier?: string | null;
  productType?: string | null;
  benefitServiceId?: string | null;
  networkTierDisplayOrderCd?: string;
  networkTierDisplayOrderCdDescription?: string;
  networkTierLabelName?: string;
}

export interface DentalAccumulation extends Accumulation {
  networkName: string;
  networkId: string;
  planPeriodCode: string;
  superNetworkId?: string;
}

export interface AccumulationNetwork {
  networkType: string;
  tier: string;
  networkTierDisplayOrderCdDescription?: string;
}

export interface PrimaryCareDetails {
  pcpStatus: string;
  hasPCP: boolean;
  primaryCarePhysician: PrimaryCareServices | null;
  providerOrganization: ProviderOrganization | null;
}

export interface ProviderOrganization {
  name: string;
  groupNumber: string;
  serviceTypeCode: string;
  addresses: Address[];
  phones: Phone[];
  assignmentType?: string;
}

export interface PrimaryCareServices {
  firstName: string;
  lastName: string;
  middleName: string;
  fullName: string;
  addresses: Address[];
  phones: Phone[];
  effectiveFrom: string;
  effectiveTill: string;
  assignmentType: string;
}

export interface MaternityBenefitDetails {
  healthWellnessProgram: string;
  newBornChargeType: string;
  newBornCoverageDuration: string;
  newBornCoverageProvision: string;
  newBornCoverageType: string;
  hasMaternity: boolean;
}

export interface HealthWellness {
  healthWellnessIndicator: string;
  clinicalExpansion: string;
}
export interface PlanAmount {
  featureType: string;
  inNetworkValue: string;
  outNetworkValue: string;
  featureTypeDesc: string;
}

export interface TierBenefit {
  tierType: string;
  tierTypeDesc: string;
  tiersList: TierList[];
}

export interface TierList {
  tierNumber: string;
  tierCopayTimePeriod: string;
  tierValue: string;
  tierLabel: string;
}

export interface PharmacyBenefit {
  planAmountList: PlanAmount[] | null;
  tierBenefitsList: TierBenefit[] | null;
  benefitLabels: string[] | null;
  accumumatorIndicators: string;
  benefitSvcCode: string | null;
  buyUpMsg: string;
  carveoutVendorName: string;
  classCode: string;
  combDedAccum: string;
  combDedOopInd: string;
  errorCode: string | null;
  eshdRetlFillLmt: string;
  fills30DayPermtdNum: string;
  footnote: string | null;
  mailOrderSpecialityDrugDays: string | null;
  mailOrderSupplyDays: string | null;
  mndtry90DayInd: string;
  noOfRefillsPermitted: string | null;
  nonSpltyProgTypeCodes: string | null;
  planCombMedRxInd: string;
  planType: string;
  prescriptionDrugListCode: string;
  retail90DayProgramType: string | null;
  retailSpecialityDrugDays: string | null;
  retailSupplyDays: string | null;
  retl90DaySuplyNum: string | null;
  rxAnnAccumCode: string;
  rxApplMOInd: string;
  rxBuyUp: string | null;
  rxCarveOutVendorName: string;
  rxClinicalProgCodePA: string | null;
  rxClinicalProgCodeTE: string | null;
  rxClmCoinsPct: string | null;
  rxDedAccumCode: string;
  rxExclDedInd: string | null;
  rxMedAccessOptCode: string | null;
  rxOopAccumCode: string;
  rxPlanDesignType: string | null;
  rxPlanType: string | null;
  spltyProgTypeCodes: string | null;
  stateCode: string | null;
  subClassCode: string | null;
  fundingArrangementType: string;
  situsStateCode: string | null;
  progTypeUMCodes: string | null;
  isInNetworkOnly: boolean;
  isOutNetworkOnly: boolean;
  isRetail90DayCoverage: boolean;
  isRxDedInd: boolean;
  isRxMaxInd: boolean;
  isErisaApplicable: boolean;
  drugListURL: string;
  coverageDetailSummary: {
    effectiveDate: string | null;
    cancelDate?: string | null;
    coverageType: string | null;
    identifier: string | null;
    clientId: string | null;
    clientName: string | null;
    groupNumber: string | null;
    groupName: string | null;
    locationCode: string | null;
    productType: string | null;
    familyCode?: string | null;
    embeddedCode?: string | null;
    isRetired: 'true' | 'false' | null;
    claimRoutings: Array<{
      claimSystemCode: string;
      memKeyType?: string;
      memKeyValue?: string;
    }> | null;
    complianceTypes: { complianceType: any[] | null };
    networkId?: string | null;
    networkType?: string | null;
    isIFP?: boolean;
    branchCode?: string;
    benOptCode: string | null;
    coverageStatus?: string | null;
    fromIBORCoverage: boolean;
    clinicalSystemLocations: any[];
  };
}

export interface DentalPlanBenefits {
  services: DentalPlanService[];
}

export interface DentalPlanService {
  classCode: number;
  classCodeDesc: string;
  benefits: {
    [key: string]: DentalAccumulationDetail | undefined;
  };
}

export interface PharmacyBenefitDetails {
  sourceSystemCode: string;
  enableAccredoLink: string;
  pharmacyBenefits: PharmacyBenefit[];
}

export interface PharmacyRequestBody {
  internalProcessingId: string;
  benefitOptionCode: string;
  erisaApplicable: boolean;
  carveOutVendorName: string;
}

export interface PharmacyResponse {
  metadata: MetaData;
  pharmacyBenefitDetails: PharmacyBenefitDetails;
}

export interface PharmacyFacetRequest {
  compositeKey: string;
  coverage: string;
  asOfDate: string;
}

export interface FrequencyAgePlanDetails {
  showBenefitPDF: boolean;
  benefitPDFName: string;
  specificCoverage: SpecificCoverage;
  procedureList: ProcedureListItem[];
}
export interface SpecificCoverage {
  frequencyAgeLimitType: string;
  frequencyAgeLimitCoverageList: FrequencyCoverageItem[];
}
export interface BaseFrequencyItem {
  code: string;
  label: string;
  inNVal: string;
  ooNVal: string;
}
export interface FrequencyCoverageItem extends BaseFrequencyItem {
  freqAgeLimitTxt: string;
}
export interface ProcedureListItem extends BaseFrequencyItem {
  indmnVal: string;
  freqAgeLimitTxt: string;
}

export interface VisionServices {
  services: VisionServicesDetails[];
  hasCoreBenefits: boolean;
  standAloneVision: boolean;
  vendorName: string;
}

export interface VisionServicesDetails {
  name: string;
  coinsurance: string;
  coPayment: string;
  maximumVisits: string;
  maximumAmount: string;
  frequency: string;
}

export interface InfertilityServices {
  services: InfertilityServicesDetails[];
  isUnlimitedExists: boolean;
}

export interface InfertilityServicesDetails {
  category: string;
  inNetworkValue: string;
  outNetworkValue: string;
}

export interface InfertilityDetail {
  networkType: string;
  planOptionType: string;
  effectiveDate: string;
  cancelDate: string;
  lifeTimeMaxAmount: string;
}

export interface SureFitRequest {
  compositeKey: string;
  coverage: string;
  asOfDate: string;
}
export interface SureFitResponse {
  pharmacyPlanResponse: PharmacyPlanResponse;
}

export interface PharmacyPlanResponse {
  pharmacyPlanDetail: PharmacyPlanDetail;
}

export interface PharmacyPlanDetail {
  networkInfo: NetworkInfo;
}

export interface NetworkInfo {
  code: string;
  description: string;
}

export interface BRNPayload {
  brn: string;
  sso: string;
  firstName: string;
  lastName: string;
  benefits: string[];
  status: string;
  notes: string;
  asOfDate: string;
  created: string;
  lastModified: string;
}

export interface PageNavbarItem {
  url: string | any[];
  label: string;
  translateLabel: boolean;
  extras?: NavigationExtras;
  disable?: boolean;
}

export interface ProviderDetailsRequest {
  provId: string;
  tin: string;
}

export interface ProviderDetails {
  data: {
    firstName: string;
    lastName: string;
    fullName: string;
    contract: {
      contracted: boolean;
      effectiveFrom: string;
      endsOn: string;
    };
    type: string;
    id: string;
    idType: string[];
    organization: {
      name: string;
    };
    networks: Network[];
    specialties?: Specialty[];
  };
  type: string;
}

export interface Specialty {
  id: string;
  name: string;
}

export interface Network {
  id: string;
  name: string;
  typeCode: string;
  typeDesc: string;
  npos: string;
  netStateCode?: string;
  networkId?: string;
}

export interface AmInNetworkStatus {
  status: boolean;
  accumListCheckCSN: boolean;
}

export interface AuthRequiredData {
  networkType: string;
  required: string;
  authorizationProvider?: string;
  reasonProAuth?: string;
}

//  TODO: resolve CHCP disables by bot
//  eslint-disable-next-line @typescript-eslint/naming-convention
export const enum priorAuthRequired {
  unknown = 'U',
  no = 'N',
  yes = 'Y',
}

export interface AccumulationAdjusted {
  key: string;
  isTier: string;
  data: AccumlationData[];
}

export interface AccumlationData {
  key: string;
  data: Accumulation;
}

export interface TableHeader {
  key: string;
  width: number;
  colspan?: number;
}

export interface TableBody {
  [key: string]: Accumulation[][];
}

export interface TableData {
  head?: TableHeader[][];
  body: Accumulation[][];
  enhancedBody?: TableBody[];
  accumNotes: string[];
  tags: string[];
  specialty?: string;
  isPriorAuthRequired?: AuthRequiredData[];
}

export interface CsnData {
  locationName: string;
  benefits: CsnNetworkAccumulations[];
  accumNotes: string[];
  specialty?: string;
  isOpen: boolean;
}

export interface CsnNetworkAccumulations {
  networkType: string;
  networkData: CsnDescAccumulations[];
}

export interface CsnDescAccumulations {
  descType: string;
  descData: Accumulation[];
}
export interface MedicareMedicalDetail {
  benefitDetails: MedicareBenefitDetail[];
  benefitOptionCode?: string;
  benefitPlanId?: string;
  benefitOptioncode?: string;
  contractNumber?: string;
  egwpAccountNumber?: string;
  id?: string;
  networkCategoryKeyCode?: string;
  pbpNumber?: string;
  planName?: string;
  planType?: string;
  segmentNumber?: string;
  planKey?: string;
}

export interface MedicareBenefitDetail {
  productBenefitType: string;
  categories?: MedicareBenefitCategory[];
}

export interface MedicareBenefitCategory {
  categoryName?: string;
  order?: string;
  categoryDescription?: Description[];
  benefits?: MedicareBenefit[];
  pbpCategoryId?: string;
}

export interface Description {
  type?: string;
  value?: string[];
}

export interface MedicareBenefit {
  name?: string;
  order?: string;
  description?: Description[];
  costShare: MedicareCostShare[];
  pbpCategoryId?: string;
}

export interface MedicareCostShare {
  code: string;
  value: string;
  order: string;
  showPartBDeductible?: string;
  description: Description[];
}

export const accumulationInit = {
  desc: '',
  amount: '',
  met: '',
  remaining: '',
  covers: '',
  notes: [],
  cignaCareNetwork: false,
  coveredServices: [],
  tierIndex: null,
  tier: null,
  productType: '',
};

export const accumulationOONInit: Accumulation = {
  ...accumulationInit,
  networkType: 'OON',
};

export const accumulationINNInit: Accumulation = {
  ...accumulationInit,
  networkType: 'INN',
};

export const accumulationPreferredInit: Accumulation = {
  ...accumulationInit,
  networkType: 'PREFERRED',
};
